// Pure domain logic for the Speech Audiometry tab — dependency-free so
// the live editor and printable report share the same defaults and
// derivations. Follows the tinnitogramLogic.js / specialLogic.js
// contract used elsewhere in the diagnostics module.

export const TEST_MATERIAL_OPTIONS = [
  'Recorded PB Word Lists (English)',
  'Recorded PB Word Lists (Hindi)',
  'Recorded PB Word Lists (Telugu)',
  'Recorded PB Word Lists (Other)',
];

export const PRESENTATION_OPTIONS = [
  'Monitored Live Voice',
  'Computer',
  'Monitored Live Voice / Computer',
];

export const ENV_OPTIONS = ['Sound Treated Booth', 'Free Field', 'Other'];
export const TRANSDUCER_OPTIONS = ['Insert Earphones', 'Supra-aural', 'Bone Conductor'];

export const DEFAULT_STANDARD = 'ANSI S3.6-2018';
export const DEFAULT_WRS_INTENSITIES = [20, 40, 60, 80, 100];

// Y-axis extents.
export const SRT_MIN_DB = 0;
export const SRT_MAX_DB = 60;    // inverted axis: 0 at top, 60 at bottom
export const WRS_MAX_DB = 100;   // X axis for WRS graph
export const PCT_MAX    = 100;

// Immutable setter helper -------------------------------------------
const cloneWith = (obj, path, value) => {
  const [head, ...rest] = path;
  const next = { ...(obj || {}) };
  if (rest.length === 0) {
    if (value === null || value === undefined || value === '') delete next[head];
    else next[head] = value;
  } else next[head] = cloneWith(next[head] || {}, rest, value);
  return next;
};

const numOrNull = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

// ── Normalise arbitrary saved data → strict shape ──────────────────
export function normaliseSpeech(data) {
  const d = data && typeof data === 'object' ? data : {};

  // Read SRT from either the new shape (`srt.right`) OR the legacy
  // `fields.srt_r` / `fields.srt_l` string bag from the old tab.
  const legacyF = d.fields || {};
  const srtRight = numOrNull(d.srt?.right ?? legacyF.srt_r);
  const srtLeft  = numOrNull(d.srt?.left  ?? legacyF.srt_l);

  // WRS: prefer new `wrs.right` map; fall back to the legacy list-of-
  // points `wrs_right: [{db_hl, percent}]`.
  const wrsMap = (list, ear) => {
    if (d.wrs && d.wrs[ear] && typeof d.wrs[ear] === 'object') {
      const out = {};
      Object.entries(d.wrs[ear]).forEach(([k, v]) => {
        const db = Number(k); const pct = numOrNull(v);
        if (Number.isFinite(db) && pct !== null) out[String(db)] = pct;
      });
      return out;
    }
    // legacy list
    const legacy = Array.isArray(list) ? list : [];
    const out = {};
    legacy.forEach((p) => {
      const db = Number(p?.db_hl); const pct = numOrNull(p?.percent);
      if (Number.isFinite(db) && pct !== null) out[String(db)] = pct;
    });
    return out;
  };

  const wrsRight = wrsMap(d.wrs_right, 'right');
  const wrsLeft  = wrsMap(d.wrs_left,  'left');

  // Intensity rows keep the 20/40/60/80/100 default, plus any custom
  // rows the audiologist added.
  const explicitRows = Array.isArray(d.wrs_rows) ? d.wrs_rows.map(Number).filter(Number.isFinite) : [];
  const derivedRows = [
    ...Object.keys(wrsRight).map(Number),
    ...Object.keys(wrsLeft).map(Number),
  ];
  const rowSet = new Set([...DEFAULT_WRS_INTENSITIES, ...explicitRows, ...derivedRows]);
  const wrs_rows = Array.from(rowSet).filter((n) => Number.isFinite(n)).sort((a, b) => a - b);

  const pbMaxRight = numOrNull(d.pb_max?.right);
  const pbMaxLeft  = numOrNull(d.pb_max?.left);

  return {
    test_material: TEST_MATERIAL_OPTIONS.includes(d.test_material) ? d.test_material : TEST_MATERIAL_OPTIONS[0],
    presentation: PRESENTATION_OPTIONS.includes(d.presentation) ? d.presentation : PRESENTATION_OPTIONS[2],
    standard: d.standard || DEFAULT_STANDARD,
    environment: ENV_OPTIONS.includes(d.environment) ? d.environment : ENV_OPTIONS[0],
    transducer: TRANSDUCER_OPTIONS.includes(d.transducer) ? d.transducer : TRANSDUCER_OPTIONS[0],
    clinician: d.clinician || '',
    refer_by:  d.refer_by  || legacyF.refer_by || '',
    srt: { right: srtRight, left: srtLeft },
    wrs: { right: wrsRight, left: wrsLeft },
    wrs_rows,
    pb_max: { right: pbMaxRight, left: pbMaxLeft },
    clinical_interpretation: d.clinical_interpretation || '',
    recommendations:         d.recommendations || '',
    fields: d.fields || {},
  };
}

// ── Setters (return NEW state; caller passes to onChange) ─────────
export const setSRT = (state, ear, dbHL) =>
  cloneWith(state, ['srt', ear], numOrNull(dbHL));

export const setWRS = (state, ear, intensityDb, percent) =>
  cloneWith(state, ['wrs', ear, String(intensityDb)], numOrNull(percent));

export const setPBMax = (state, ear, percent) =>
  cloneWith(state, ['pb_max', ear], numOrNull(percent));

export const addWrsIntensityRow = (state, dbHL) => {
  const db = Number(dbHL);
  if (!Number.isFinite(db)) return state;
  if ((state.wrs_rows || []).includes(db)) return state;
  const rows = [...(state.wrs_rows || []), db].sort((a, b) => a - b);
  return { ...state, wrs_rows: rows };
};

export const removeWrsIntensityRow = (state, dbHL) => {
  const db = Number(dbHL);
  const rows = (state.wrs_rows || []).filter((r) => r !== db);
  const nextRight = { ...state.wrs.right }; delete nextRight[String(db)];
  const nextLeft  = { ...state.wrs.left  }; delete nextLeft[String(db)];
  return { ...state, wrs_rows: rows, wrs: { right: nextRight, left: nextLeft } };
};

// ── Auto-derivations ──────────────────────────────────────────────
export function autoPbMax(state, ear) {
  const override = state.pb_max?.[ear];
  if (override !== null && override !== undefined) return Number(override);
  const map = state.wrs?.[ear] || {};
  let best = null;
  Object.values(map).forEach((v) => {
    const n = Number(v);
    if (Number.isFinite(n)) best = best === null ? n : Math.max(best, n);
  });
  return best;
}

export function wrsClass(pct) {
  if (pct === null || pct === undefined || !Number.isFinite(Number(pct))) return null;
  const p = Number(pct);
  if (p >= 90) return 'Excellent';
  if (p >= 75) return 'Good';
  if (p >= 60) return 'Fair';
  return 'Poor';
}

// Rollover check — significant drop (>=20%) from PB Max at any louder
// intensity level within the same ear.
export function hasRollover(state, ear) {
  const map = state.wrs?.[ear] || {};
  const entries = Object.entries(map)
    .map(([db, pct]) => ({ db: Number(db), pct: Number(pct) }))
    .filter((p) => Number.isFinite(p.db) && Number.isFinite(p.pct))
    .sort((a, b) => a.db - b.db);
  if (entries.length < 2) return false;
  let peak = -Infinity, peakDb = -Infinity;
  for (const p of entries) {
    if (p.pct > peak) { peak = p.pct; peakDb = p.db; }
  }
  for (const p of entries) {
    if (p.db > peakDb && peak - p.pct >= 20) return true;
  }
  return false;
}

export function buildClinicalInterpretation(state) {
  const bullets = [];
  const srtR = state.srt?.right; const srtL = state.srt?.left;
  if (srtR !== null && srtR !== undefined) {
    bullets.push('Speech Reception Threshold (SRT) is in agreement with pure-tone thresholds.');
  }
  const pbR = autoPbMax(state, 'right');
  const pbL = autoPbMax(state, 'left');
  const cR = wrsClass(pbR); const cL = wrsClass(pbL);
  if (cR && cL) {
    bullets.push(`Word recognition scores are ${cR.toLowerCase()} in the right ear and ${cL.toLowerCase()} in the left ear.`);
  } else if (cR) {
    bullets.push(`Word recognition score is ${cR.toLowerCase()} in the right ear.`);
  } else if (cL) {
    bullets.push(`Word recognition score is ${cL.toLowerCase()} in the left ear.`);
  }
  const roR = hasRollover(state, 'right');
  const roL = hasRollover(state, 'left');
  if (roR || roL) {
    const ears = [roR ? 'right' : null, roL ? 'left' : null].filter(Boolean).join(' and ');
    bullets.push(`Rollover observed in the ${ears} ear.`);
  } else if (pbR !== null || pbL !== null) {
    bullets.push('No evidence of significant rollover observed.');
  }
  bullets.push('Results suggest normal to near-normal speech understanding at supra-threshold levels.');
  return bullets.join('\n');
}

export function buildRecommendations() {
  return [
    'Correlate with pure-tone audiometry and case history.',
    'Consider amplification evaluation if communicative difficulties are reported.',
    'Continue audiological monitoring as advised.',
  ].join('\n');
}
